'use client';

import { 
  ControlType, 
  CalculatorId, 
  CalculatorType, 
  ControlIds, 
  type CalculatorConfig, 
  type ControlValues,
  type MultiColumnSelectControl,
  type TextControl,
  type SelectControl,
  type NumberControl,
  type SingleColumnSelectControl
} from '../../../lib/calculators/calculator-config';
import { TableDataJSON } from '../../../src/types/table-json';
import { LazyTabLabECharts } from '@repo/ui';
import type { EChartsOption } from 'echarts';

// Type for timeseries specific control values
export interface TimeseriesControlValues extends ControlValues {
  [ControlIds.XColumn]: string;
  [ControlIds.Values]: string[];
  [ControlIds.Title]: string;
  [ControlIds.ShowArea]: string;
  [ControlIds.AreaOpacity]: string;
  [ControlIds.MarkerSize]: string;
}

// Define the controls for the timeseries chart
export const timeseriesControls = {
  tabs: [
    {
      title: 'Data',
      controls: [
        {
          id: ControlIds.XColumn,
          type: ControlType.SingleColumnSelect,
          label: 'Time column',
          required: true
        } satisfies SingleColumnSelectControl,
        {
          id: ControlIds.Values,
          type: ControlType.MultiColumnSelect,
          label: 'Value columns',
          required: true,
          numericOnly: true,
          minSelections: 1
        } satisfies MultiColumnSelectControl,
        {
          id: ControlIds.Title,
          type: ControlType.Text,
          label: 'Chart title',
          required: false
        } satisfies TextControl
      ]
    },
    {
      title: 'Appearance',
      controls: [
        {
          id: ControlIds.ShowArea,
          type: ControlType.Select,
          label: 'Show area',
          options: [
            { label: 'Yes', value: 'true' },
            { label: 'No', value: 'false' }
          ],
          defaultValue: 'false'
        } satisfies SelectControl,
        {
          id: ControlIds.AreaOpacity,
          type: ControlType.Number,
          label: 'Area opacity',
          min: 0,
          max: 1,
          step: 0.1,
          defaultValue: 0.3
        } satisfies NumberControl,
        {
          id: ControlIds.MarkerSize,
          type: ControlType.Number,
          label: 'Marker size',
          min: 0,
          max: 20,
          step: 1,
          defaultValue: 6
        } satisfies NumberControl
      ]
    }
  ]
};

// Helper function to parse and format dates
function parseDateValue(value: string): Date | null {
  const date = new Date(value);
  return isNaN(date.getTime()) ? null : date;
}

// Pure function to generate ECharts config for a timeseries chart
export function getTimeseriesEChartsConfig({
  timeData,
  series,
  title,
  showArea,
  areaOpacity,
  markerSize
}: {
  timeData: (Date | null)[];
  series: { name: string; data: number[] }[];
  title?: string;
  showArea: boolean;
  areaOpacity: number;
  markerSize: number;
}): EChartsOption {
  return {
    title: {
      text: title || 'Time Series Chart',
      left: 'center',
      top: 2,
      padding: [0, 0, 0, 0]
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross'
      }
    },
    legend: {
      data: series.map(s => s.name),
      top: 25
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      top: '15%',
      containLabel: true
    },
    toolbox: {
      feature: {
        saveAsImage: { title: 'Save' },
        dataZoom: {
          title: {
            zoom: 'Zoom',
            back: 'Reset Zoom'
          }
        }
      }
    },
    xAxis: {
      type: 'time',
      boundaryGap: [0, 0],
      axisLabel: {
        formatter: (value: number) => {
          const date = new Date(value);
          return date.toLocaleDateString();
        }
      }
    },
    yAxis: {
      type: 'value',
      splitLine: {
        lineStyle: {
          type: 'dashed'
        }
      }
    },
    dataZoom: [
      {
        type: 'slider',
        show: true,
        xAxisIndex: [0],
        start: 0,
        end: 100
      },
      {
        type: 'inside',
        xAxisIndex: [0],
        start: 0,
        end: 100
      }
    ],
    series: series.map(s => ({
      name: s.name,
      type: 'line',
      data: s.data.map((value, index) => {
        const timestamp = timeData[index]?.getTime();
        return timestamp ? [timestamp, value] : null;
      }).filter(Boolean),
      smooth: true,
      symbol: 'circle',
      symbolSize: markerSize,
      areaStyle: showArea ? {
        opacity: areaOpacity
      } : undefined
    }))
  };
}

export const timeseriesConfig: CalculatorConfig<CalculatorId.TimeseriesChart> = {
  id: CalculatorId.TimeseriesChart,
  type: CalculatorType.Graph,
  name: 'Time Series Chart',
  initialData: TableDataJSON.from({
    columns: [
      { 
        id: 'date', 
        name: 'Date', 
        values: [
          '2023-01-01', '2023-02-01', '2023-03-01', '2023-04-01', 
          '2023-05-01', '2023-06-01', '2023-07-01', '2023-08-01', 
          '2023-09-01', '2023-10-01', '2023-11-01', '2023-12-01'
        ] 
      },
      { 
        id: 'revenue', 
        name: 'Revenue ($K)', 
        values: ['120', '125', '135', '140', '150', '160', '175', '180', '170', '165', '155', '150'] 
      },
      {
        id: 'users',
        name: 'Active Users (K)',
        values: ['50', '52', '55', '58', '62', '65', '70', '72', '75', '78', '80', '82']
      }
    ]
  }),
  initialControlValues: {
    [ControlIds.XColumn]: 'date',
    [ControlIds.Values]: ['revenue', 'users'],
    [ControlIds.Title]: 'Revenue and User Growth Over Time',
    [ControlIds.ShowArea]: 'false',
    [ControlIds.AreaOpacity]: '0.3',
    [ControlIds.MarkerSize]: '6'
  },
  controls: timeseriesControls,
  getInitialConfig: (data: TableDataJSON) => {
    const dateColumns = data.columns.filter(col => 
      col.values.some(val => parseDateValue(val) !== null)
    );
    const numericColumns = data.columns.filter(col => 
      col.values.every(val => !isNaN(Number(val)))
    );

    return {
      [ControlIds.XColumn]: dateColumns[0]?.id || data.columns[0]?.id,
      [ControlIds.Values]: numericColumns.slice(0, 2).map(col => col.id),
      [ControlIds.Title]: 'Time Series Chart',
      [ControlIds.ShowArea]: 'false',
      [ControlIds.AreaOpacity]: '0.3',
      [ControlIds.MarkerSize]: '6'
    };
  },
  getVisualizationData: ({ data, controls }) => {
    const timeseriesControls = controls as TimeseriesControlValues;
    
    if (!data?.columns?.length || !timeseriesControls[ControlIds.Values]?.length) {
      return null;
    }

    const timeColumn = data.columns.find(col => col.id === timeseriesControls[ControlIds.XColumn]);
    const selectedColumns = timeseriesControls[ControlIds.Values]
      .map(id => data.columns.find(col => col.id === id))
      .filter((col): col is typeof data.columns[0] => col !== undefined);

    if (!timeColumn || !selectedColumns.length) {
      return null;
    }

    const timeData = timeColumn.values.map(parseDateValue);
    const series = selectedColumns.map(col => ({
      name: col.name,
      data: col.values.map(v => Number(v))
    }));

    return getTimeseriesEChartsConfig({
      timeData,
      series,
      title: timeseriesControls[ControlIds.Title],
      showArea: timeseriesControls[ControlIds.ShowArea] === 'true',
      areaOpacity: Number(timeseriesControls[ControlIds.AreaOpacity]),
      markerSize: Number(timeseriesControls[ControlIds.MarkerSize])
    });
  },
  render: ({ visualizationData }) => 
    visualizationData ? (
      <LazyTabLabECharts option={visualizationData} className="h-full w-full" />
    ) : null
}; 